import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BillingDetailModal from '../BillingDetailModal/BillingDetailModal';
import BillingModuleListingCard from '../BillingModuleListingCard/BillingModuleListingCard';
import BilllingListCardSkeletonLoader from '../BilllingListCardSkeletonLoader/BilllingListCardSkeletonLoader';
import useDebounce from 'hooks/useDebounce';
import useResetData from 'hooks/useResetData';
import { getStorageBillingInvoices } from 'services/billing/storageBillingInvoices.service';
import useFiltersState from 'state/useFiltersState';
import { IStorageBillingInvoices, IStorageBillingInvoicesGroupedByMonth } from 'types/billing.types';
import searchIcon from '../../../../assets/images/catalogue/searchIcon.png';
import { ICountryDetail } from 'types/bussines';
import { DateTime } from 'luxon';
import useSWRInfinite from 'swr/infinite';
import './BillingListingTableContent.scss';

interface IBillingListingTableContentProps {
  searchText: string;
  listOfCountries: ICountryDetail[];
  onAvailableMonthsChange?: (months: string[], monthValueMap: Map<string, string>) => void;
}

const BillingListingTableContent = ({
  searchText,
  listOfCountries,
  onAvailableMonthsChange
}: IBillingListingTableContentProps) => {
  const { billingModuleListingFilters, setBillingModuleListingFilters } = useFiltersState();
  const { resetStorageDataWithLoaders, resetStorageDataWithLoadersState } = useResetData();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [showModalSide, setShowModalSide] = useState(false);
  const [billingInvoiceSelected, setBillingInvoiceSelected] = useState<number | null>(null);
  const [persistedAvailableMonths, setPersistedAvailableMonths] = useState<string[]>([]);
  const [persistedMonthValueMap, setPersistedMonthValueMap] = useState<Map<string, string>>(new Map());

  const debouncedSearch = useDebounce(searchText, 800);

  const hasMorePages = useCallback((groupedData: IStorageBillingInvoicesGroupedByMonth): boolean => {
    return Object.values(groupedData).some(monthInvoices => monthInvoices?.length > 0);
  }, []);

  const filtersString = useMemo(() => {
    const filters = billingModuleListingFilters.reduce((acc: { [key: string]: string[] }, filter) => {
      acc[filter?.name] = filter?.values;
      return acc;
    }, {} as { [key: string]: string[] });
    return Object.keys(filters)?.length ? JSON.stringify(filters) : '';
  }, [billingModuleListingFilters]);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: IStorageBillingInvoicesGroupedByMonth | null) => {
      if (previousPageData && !hasMorePages(previousPageData)) return null;
      const searchParam = debouncedSearch ? `&search=${debouncedSearch}` : '';
      const filtersParam = filtersString ? `&filters=${filtersString}` : '';
      return `/storage-invoice/all-invoices?page=${pageIndex}&limit=100${searchParam}${filtersParam}`;
    },
    [debouncedSearch, filtersString, hasMorePages]
  );

  const {
    data: pages = [],
    isLoading,
    isValidating,
    size,
    setSize,
    mutate
  } = useSWRInfinite(getKey, getStorageBillingInvoices, {
    revalidateFirstPage: false,
    revalidateOnFocus: false
  });

  const hasMonthFilter = useMemo(() => {
    return billingModuleListingFilters.some(filter => filter?.name === 'months');
  }, [billingModuleListingFilters]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const threshold = 100;
    const remainingScroll = scrollHeight - scrollTop - clientHeight;
    const isNearingBottom = remainingScroll > 0 && remainingScroll < threshold;

    setIsNearBottom(isNearingBottom);
  }, []);

  const currentMonthsFromPages = useMemo(() => {
    const monthsMap = new Map<string, string>();
    const valueMap = new Map<string, string>();

    pages.forEach(page => {
      Object.keys(page).forEach(monthKey => {
        if (monthKey && !monthsMap.has(monthKey)) {
          const [year, month] = monthKey.split('-');
          if (year && month) {
            const date = DateTime.fromObject({ year: Number(year), month: Number(month) });
            const formatted = date.setLocale('es').toFormat('LLLL yyyy');
            const formattedMonth = formatted.charAt(0).toUpperCase() + formatted.slice(1);
            monthsMap.set(monthKey, formattedMonth);
            valueMap.set(formattedMonth, monthKey);
          }
        }
      });
    });

    const sortedMonths = Array.from(monthsMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, formattedMonth]) => formattedMonth);

    return { availableMonths: sortedMonths, monthValueMap: valueMap };
  }, [pages]);

  const availableMonths =
    persistedAvailableMonths?.length > 0 ? persistedAvailableMonths : currentMonthsFromPages?.availableMonths;
  const monthValueMap =
    persistedMonthValueMap?.size > 0 ? persistedMonthValueMap : currentMonthsFromPages?.monthValueMap;

  const groupedInvoices = useMemo(() => {
    const allMonthsMap: IStorageBillingInvoicesGroupedByMonth = {};

    pages.forEach(page => {
      Object.entries(page).forEach(([monthKey, invoices]) => {
        if (!allMonthsMap[monthKey]) {
          allMonthsMap[monthKey] = [];
        }
        allMonthsMap[monthKey].push(...invoices);
      });
    });

    return Object.keys(allMonthsMap)
      .sort((a, b) => b.localeCompare(a))
      .flatMap(monthKey => allMonthsMap[monthKey]);
  }, [pages]);

  const handleModalSide = useCallback((billingInvoiceId: number) => {
    if (!billingInvoiceId) return;
    setBillingInvoiceSelected(billingInvoiceId);
    setShowModalSide(prev => !prev);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModalSide(false);
    resetStorageDataWithLoaders();
  }, [resetStorageDataWithLoaders]);

  useEffect(() => {
    if (!hasMonthFilter && currentMonthsFromPages?.availableMonths?.length > 0) {
      setPersistedAvailableMonths(currentMonthsFromPages?.availableMonths);
      setPersistedMonthValueMap(currentMonthsFromPages?.monthValueMap);
    }
  }, [hasMonthFilter, currentMonthsFromPages]);

  useEffect(() => {
    if (onAvailableMonthsChange && !!availableMonths?.length) {
      onAvailableMonthsChange(availableMonths, monthValueMap);
    }
  }, [availableMonths, monthValueMap, onAvailableMonthsChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!isNearBottom || isLoading || isValidating || pages?.length === 0) return;

    const lastPage = pages[pages?.length - 1];
    if (hasMorePages(lastPage)) {
      setSize(size + 1);
    }
  }, [isNearBottom, isLoading, isValidating, pages, setSize, size, hasMorePages]);

  useEffect(() => {
    if (resetStorageDataWithLoadersState) {
      mutate();
    }
  }, [resetStorageDataWithLoadersState, mutate]);

  useEffect(() => {
    return () => {
      setBillingModuleListingFilters([]);
    };
  }, [setBillingModuleListingFilters]);

  return (
    <div ref={containerRef} className="billingListingTableContent">
      {showModalSide && billingInvoiceSelected && (
        <BillingDetailModal
          billingInvoiceId={billingInvoiceSelected}
          countryList={listOfCountries}
          isOpen={showModalSide}
          onClose={handleCloseModal}
        />
      )}
      {!isLoading && !groupedInvoices?.length && (
        <div className="notFound">
          <div className="verticalCenter">
            <div className="boxNoFound">
              <div className="positionIcon">
                <img className="sizeImg" alt="notFoundImg" src={searchIcon} />
              </div>
              <div className="textNoFound">No hay resultados para tu busqueda </div>
            </div>
          </div>
        </div>
      )}
      {groupedInvoices?.map((billingInvoice: IStorageBillingInvoices) => (
        <BillingModuleListingCard
          key={billingInvoice?.storageInvoiceId}
          billingInvoice={billingInvoice}
          handleModalSide={handleModalSide}
          countryList={listOfCountries}
        />
      ))}
      {(isLoading || isValidating) &&
        Array.from({ length: 20 }, (_, index) => (
          <BillingModuleListingCard
            key={`skeleton-${index}`}
            isLoading
            handleModalSide={handleModalSide}
            countryList={listOfCountries}
          />
        ))}
    </div>
  );
};

export default BillingListingTableContent;
